import { get, isEmpty } from 'lodash';
import {
  convertObjToCamelCase,
  convertObjToSnakeCase,
  retrieveDataMapper,
  retrieveObjectMapper,
  retrieveSortForMS,
} from 'utils';
import { centreSerivce, MS_PHASE_3_ENDPOINTS } from 'utils/msEndpointConstants';
import { DEFAULT_ERROR } from 'utils/constants';
import DatadogHandler from 'utils/datadog';
import { getHolidayOfYearForCalendar } from './CentreService';

const fileName = 'src/services/CalendarService.js';
const { CENTRE } = MS_PHASE_3_ENDPOINTS;

export const msFetchHolidays = async (_, reqBody) => {
  try {
    const filter = get(reqBody, 'filter', {});
    const resp = await centreSerivce().post(
      `${CENTRE.getCentreHolidaysOfYear}?${new URLSearchParams({
        page: get(reqBody, 'pagination.page', 1),
        perPage: get(reqBody, 'pagination.perPage', 10),
        ...retrieveSortForMS(get(reqBody, 'pagination.sort', []), 'sort'),
      })}`,
      {
        ...(get(reqBody, 'IDCentres.length', 0) > 0 && {
          centre_ids: get(reqBody, 'IDCentres'),
        }),
        ...(get(reqBody, 'year') && {
          year: get(reqBody, 'year'),
        }),
        ...(get(reqBody, 'month') && {
          month: get(reqBody, 'month'),
        }),
        ...(!isEmpty(filter) && {
          filter: {
            ...(get(filter, 'fkSchool') && {
              school: [{ eq: get(filter, 'fkSchool') }],
            }),
            ...(get(filter, 'isPublic') && {
              is_public: [{ eq: get(filter, 'isPublic') }],
            }),
            ...(get(filter, 'ID') && {
              id: [{ eq: get(filter, 'ID') }],
            }),
          },
        }),
      }
    );
    const data = get(resp, 'data.data') || [];
    const centres = get(resp, 'data.centres') || [];
    return {
      success: true,
      data: {
        getCentreHolidaysOfYear: {
          data: data?.map(c => ({
            ...retrieveObjectMapper(c, [
              'ID:id',
              'title',
              'description',
              'from',
              'to',
              'is_public',
              'holiday_image_key',
            ]),
            centreHolidays: {
              data: (get(c, 'centre_holidays') || []).map(ctl => ({
                centre: retrieveDataMapper(centres, ctl?.fk_centre, [
                  'ID:id',
                  'label',
                ]),
              })),
              totalCount: get(c, 'centre_holiday_total_count', 0),
            },
          })),
          totalCount: get(resp, 'data.totalCount', 0),
        },
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFetchHolidays',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msFetchAppointments = async (_, variables) => {
  try {
    const centreId = get(variables, 'IDCentre');
    const year = get(variables, 'year');
    const month = get(variables, 'month');
    const from = get(variables, 'from');
    const to = get(variables, 'to');

    const payload = {
      ...(centreId && { centre_id: centreId }),
      ...(year && { year }),
      ...(month && { month }),
      ...(from && { from }),
      ...(to && { to }),
    };

    const resp = await centreSerivce().post(
      CENTRE.findAllCalendarEvent,
      payload
    );

    const responseData = get(resp, 'data', {});
    const data = get(responseData, 'data', []);
    const totalCount = get(responseData, 'totalCount', 0);

    return {
      success: true,
      data: {
        findAllCalendarEvent: {
          totalCount,
          data: data.map(item => ({
            ...convertObjToCamelCase(item),
          })),
        },
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFetchAppointments',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(
        error,
        'response.data.error',
        get(error, 'message', DEFAULT_ERROR)
      ),
    };
  }
};

export const msFindLeaves = () => {
  return {
    data: [],
  };
};

export const msHolidaysOfYearForCalendar = async (_, reqData) => {
  try {
    const reqBody = (reqData || [])?.find(
      q => get(q, 'key') === 'getHolidaysOfYearForCalendar'
    );
    const resp = await getHolidayOfYearForCalendar({
      centreIds: get(reqBody, 'variables.0.IDCentres', []),
      year: get(reqBody, 'variables.0.year'),
    });

    return {
      totalCount: get(resp, 'totalCount', 0),
      data: get(resp, 'data', [])?.map(d => ({
        ...retrieveObjectMapper(d, [
          'ID:id',
          'title',
          'description',
          'from',
          'to',
          'is_public',
        ]),
        centreHolidays: {
          totalCount: get(d, 'centre_holiday_total_count', 0),
        },
      })),
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msHolidaysOfYearForCalendar',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msCreateAppointment = async (_, reqBody) => {
  try {
    if (!reqBody?.IDCentre) return { success: true, data: {} };

    const centreSvc = centreSerivce();
    const resp = await centreSvc.post(
      ...CENTRE.addCalendarEvent(
        convertObjToSnakeCase(reqBody, ['centre_id:IDCentre'])
      )
    );

    return {
      success: true,
      data: {
        addCalendarEvent: resp?.data && convertObjToCamelCase(resp?.data),
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msCreateAppointment',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message:
            error?.response?.data?.error || error?.message || DEFAULT_ERROR,
        },
      ],
    };
  }
};

export const msUpdateHoliday = async (_, reqData) => {
  try {
    const resp = await centreSerivce().put(
      CENTRE.updateHoliday(get(reqData, 'holidayID')),
      {
        centre_ids: get(reqData, 'centreIDList') || [],
        holiday: {
          description: get(reqData, 'updateHoliday.description', ''),
          fk_school: get(reqData, 'updateHoliday.fkSchool'),
          from: get(reqData, 'updateHoliday.from', ''),
          holiday_image_key: get(reqData, 'updateHoliday.holidayImageKey', ''),
          is_public: get(reqData, 'updateHoliday.isPublic', false),
          title: get(reqData, 'updateHoliday.title', ''),
          to: get(reqData, 'updateHoliday.to', ''),
        },
        ignore_app_notification: get(reqData, 'ignoreAppNotification', false),
      }
    );
    return {
      success: !!get(resp, 'data.id'),
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msUpdateHoliday',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            error,
            'response.data.error',
            get(error, 'message', DEFAULT_ERROR)
          ),
        },
      ],
    };
  }
};

export const msCreateHoliday = async (_, reqData) => {
  try {
    const resp = await centreSerivce().post(CENTRE.createHoliday, {
      centre_ids: get(reqData, 'centreIDList') || [],
      holiday: {
        description: get(reqData, 'holiday.description', ''),
        fk_school: get(reqData, 'holiday.fkSchool'),
        from: get(reqData, 'holiday.from', ''),
        holiday_image_key: get(reqData, 'holiday.holidayImageKey', ''),
        is_public: get(reqData, 'holiday.isPublic', false),
        title: get(reqData, 'holiday.title', ''),
        to: get(reqData, 'holiday.to', ''),
      },
      ignore_app_notification: get(reqData, 'ignoreAppNotification', false),
    });
    return {
      success: !!get(resp, 'data.id'),
      data: {
        createHoliday: convertObjToCamelCase(get(resp, 'data', {})),
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msCreateHoliday',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            error,
            'response.data.error',
            get(error, 'message', DEFAULT_ERROR)
          ),
        },
      ],
    };
  }
};

export const msRemoveHoliday = async (_, reqData) => {
  try {
    const resp = await centreSerivce().delete(
      CENTRE.removeHoliday(get(reqData, 'holidayID')),
      {
        data: {
          ignore_app_notification: get(reqData, 'ignoreAppNotification', false),
        },
      }
    );
    return {
      success: get(resp, 'data.success', false),
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msRemoveHoliday',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            error,
            'response.data.error',
            get(error, 'message', DEFAULT_ERROR)
          ),
        },
      ],
    };
  }
};
